import { useFunds } from 'hooks/data/fund/useFunds';
import { useGetRole } from './useGetRole';
import { useFirm } from 'hooks/data/firm/useFirm';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import {
  createEisSidebarNav,
  createLpSidebarNav,
  MenuItemType,
} from 'helpers/layout/sidebarMenu';
import {
  FIRM,
  createFirmEditRoute,
  createOpenTrancheRoute,
  createSingleFirmRoute,
} from 'adminConstants/routes';
import { AdminUserRealm } from 'further-types/admin-user';
import { RolePermission } from 'further-types/admin-user-role';
import { useLayoutContext } from 'contexts/LayoutContext';

export type SidebarMenuItemType = MenuItemType;

export default function useSidebarMenuItems(realm?: AdminUserRealm) {
  const { firmId } = useGetRole();
  const { vehicle } = useLayoutContext();
  const hasReadTranche = useHasPermissions([RolePermission.ReadTranche]);
  const hasReadFirm = useHasPermissions([RolePermission.ReadFirm]);
  const isEisOrSuperRealm =
    realm === AdminUserRealm.Eis || realm === AdminUserRealm.Super;

  const { tranches } = useFunds({
    fundStatus: 1,
    leanResponse: true,
    disabled: !isEisOrSuperRealm || !hasReadTranche,
  });
  const { firms } = useFirm({
    params: { disabled: !isEisOrSuperRealm || !hasReadFirm },
  });

  if (realm === AdminUserRealm.Lp) {
    return createLpSidebarNav({
      hasGlAccounts: vehicle?.hasGlAccounts ?? false,
    });
  }

  if (isEisOrSuperRealm) {
    const myFirmLink = firmId ? createFirmEditRoute(firmId) : FIRM;

    if (!tranches.data?.length) {
      return createEisSidebarNav(myFirmLink);
    }

    const openTrancheDashboardLinks = (tranches.data ?? []).map((fund) => ({
      name: `${fund.firmId.firmName} ${fund.fundName}`,
      type: 'item',
      link: createOpenTrancheRoute(fund?._id),
    }));

    if (firmId) {
      return createEisSidebarNav(myFirmLink, openTrancheDashboardLinks);
    }

    if (!firms.data?.result?.length) {
      return createEisSidebarNav(myFirmLink);
    }

    const firmDashboardLinks = firms.data.result.map((firm) => ({
      name: firm.firmName,
      type: 'item',
      link: createSingleFirmRoute(firm?._id),
    }));

    return createEisSidebarNav(
      myFirmLink,
      openTrancheDashboardLinks,
      firmDashboardLinks,
    );
  }
}
